import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { Input } from 'reactstrap';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toastAlert from '../../../js/Base/ToastAlert';
import { updateProjectScanSchedule } from '../../../api/scanScheduleApi';

const labelStyle = {
  fontSize: '12px',
  color: '#323232',
  fontWeight: 'bold',
  marginTop: '20px',
  marginBottom: '6px',
  fontFamily: 'Noto Sans, sans-serif',
};

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

const DAY_OPTIONS = range(1, 28);
const HOUR_OPTIONS = range(0, 23);
const MINUTE_OPTIONS = range(0, 59);

const pad2 = (n) => String(n).padStart(2, '0');

/** 與後端 resolve_scheduled_scan_month 同一條規則，僅供輸入當下即時預覽 */
const describeScanMonthRule = (dayOfMonth) => (Number(dayOfMonth) === 1 ? '上個月' : '當月');

const ScanScheduleEditModal = ({ isModalOpen, handleClose, schedule, submitEffect }) => {
  if (!schedule) return null;

  const initialValues = {
    day_of_month: schedule.day_of_month,
    hour: schedule.hour,
    minute: schedule.minute,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const data = await updateProjectScanSchedule({
        project_uuid: localStorage.getItem('project_uuid'),
        acct_email: localStorage.getItem('user_email'),
        scan_type: schedule.scan_type,
        day_of_month: Number(values.day_of_month),
        hour: Number(values.hour),
        minute: Number(values.minute),
      });

      if (data.success) {
        toastAlert.autoClose(true).showSuccess('儲存成功');
        handleClose();
        submitEffect();
      } else {
        toastAlert.autoClose(true).showError(data.msg || '儲存失敗，請稍後再試');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      animation="true"
      style={{ zIndex: '8999' }}
      show={isModalOpen}
      onHide={handleClose}
      dialogClassName="ProcessManagement Modal"
    >
      <Modal.Header>
        <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>
          編輯排程週期 - {schedule.label}
        </Modal.Title>
        <button
          onClick={handleClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginLeft: 'auto',
          }}
        >
          <CloseIcon fontSize="medium" />
        </button>
      </Modal.Header>

      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
        {({ values, handleChange, isSubmitting }) => (
          <Form className="form" style={{ padding: '0px' }}>
            <Modal.Body className="modal-body" style={{ paddingTop: '0px' }}>
              <p style={labelStyle}>執行日（每月）</p>
              <Input
                type="select"
                name="day_of_month"
                value={values.day_of_month}
                onChange={handleChange}
                style={{ fontSize: '13px', boxShadow: 'none' }}
              >
                {DAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>{`${d} 日`}</option>
                ))}
              </Input>

              <p style={labelStyle}>執行時間</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Input
                  type="select"
                  name="hour"
                  value={values.hour}
                  onChange={handleChange}
                  style={{ fontSize: '13px', boxShadow: 'none' }}
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h} value={h}>{`${pad2(h)} 時`}</option>
                  ))}
                </Input>
                <span style={{ fontSize: '13px', color: '#323232' }}>:</span>
                <Input
                  type="select"
                  name="minute"
                  value={values.minute}
                  onChange={handleChange}
                  style={{ fontSize: '13px', boxShadow: 'none' }}
                >
                  {MINUTE_OPTIONS.map((m) => (
                    <option key={m} value={m}>{`${pad2(m)} 分`}</option>
                  ))}
                </Input>
              </div>

              <p style={labelStyle}>時區</p>
              <Input
                type="text"
                value={schedule.tz_name}
                disabled
                style={{ fontSize: '13px', boxShadow: 'none' }}
              />

              {/* 資料月份規則不直觀，直接把結果攤開讓管理員在存檔前就看到 */}
              <Box
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#F5F7FA',
                  border: '1px solid #E2E2E2',
                }}
              >
                <Typography sx={{ fontSize: '13px', color: '#323232', lineHeight: 1.8 }}>
                  每月 <strong>{values.day_of_month}</strong> 日{' '}
                  <strong>{`${pad2(values.hour)}:${pad2(values.minute)}`}</strong>
                  （{schedule.tz_name}）執行
                  <br />
                  → 資料月份記錄為 <strong>{describeScanMonthRule(values.day_of_month)}</strong>
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#767676', mt: 0.5 }}>
                  1 日執行代表彙整前一個月的資料；2 日（含）以後執行則記錄為當月。
                </Typography>
              </Box>
            </Modal.Body>

            <Modal.Footer className="modal-footer">
              <Button type="button" className="btn btn-cancel" onClick={handleClose}>
                取消
              </Button>
              <Button type="submit" className="btn btn-custom" disabled={isSubmitting}>
                確認
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ScanScheduleEditModal;
